<template>
  <nav
    v-if="navigationReady"
    data-section-nav
    :class="props.horizontal ? 'flex h-full flex-row items-stretch' : 'flex h-full flex-col'">
    <div
      :class="
        props.horizontal
          ? props.stacked
            ? 'hide-scrollbar flex min-w-0 flex-1 items-stretch gap-0.5 overflow-x-auto overflow-y-hidden'
            : 'hide-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden'
          : 'hide-scrollbar min-h-0 flex-1 overflow-y-auto'
      ">
      <!-- Add menu (edit mode) -->
      <div v-if="isEditMode && !props.horizontal" class="relative px-1 pt-1">
        <button
          type="button"
          class="mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          @click="addMenuOpen = !addMenuOpen">
          <span class="material-symbols-outlined text-2xl">add</span>
          <span class="truncate flex-1">{{ tt('Add', 'Dodaj') }}</span>
        </button>
        <div
          v-if="addMenuOpen"
          class="glass-surface glass-inner absolute left-3 right-3 top-full z-30 mt-1 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-700">
          <button
            type="button"
            class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-black/5 dark:text-gray-200 dark:hover:bg-white/10"
            @click="startAddDashboard">
            <span class="material-symbols-outlined text-base">dashboard</span>
            {{ tt('Add dashboard', 'Dodaj nadzorno ploščo') }}
          </button>
        </div>
      </div>

      <!-- Main list -->
      <draggable
        v-if="isEditMode && !props.horizontal"
        v-model="editableRows"
        item-key="key"
        tag="ul"
        handle=".nav-drag-handle"
        class="space-y-1 py-1"
        ghost-class="opacity-40"
        :animation="150"
        @end="persistRows">
        <template #item="{ element: row }">
          <li
            class="group relative flex items-center gap-1"
            :class="row.hidden ? 'opacity-50' : ''"
            @contextmenu.prevent="row.editable ? openContextMenu(row, $event) : null">
            <button
              type="button"
              class="nav-drag-handle flex-shrink-0 cursor-move rounded-md p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-200"
              :title="tt('Drag to reorder', 'Povleci za preurejanje')">
              <span class="material-symbols-outlined text-base">drag_indicator</span>
            </button>
            <div
              class="flex min-w-0 flex-1 items-center gap-3 px-2 py-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <span class="material-symbols-outlined flex-shrink-0 text-2xl">{{ row.icon }}</span>
              <span class="truncate flex-1">{{ row.label }}</span>
            </div>
            <button
              type="button"
              class="flex-shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-black/5 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-200"
              :title="row.hidden ? tt('Show in menu', 'Pokaži v meniju') : tt('Hide from menu', 'Skrij iz menija')"
              @click="toggleRowVisible(row)">
              <span class="material-symbols-outlined text-base">{{
                row.hidden ? 'visibility_off' : 'visibility'
              }}</span>
            </button>
          </li>
        </template>
      </draggable>
      <ul v-else :class="props.stacked ? 'contents' : props.horizontal ? 'flex items-center gap-1' : 'space-y-1'">
        <li v-for="item in visibleRows" :key="item.key" :class="props.stacked ? 'min-w-16 flex-1' : ''">
          <NavButton
            variant="nav"
            :to="item.to"
            :icon="item.icon"
            :label="item.label"
            :show-label="showLabels"
            :stacked="props.stacked"
            :badge="item.badge || undefined"
            :badge-color="item.badgeColor || undefined"
            :has-submenu="item.hasSubmenu"
            :state="item.state || undefined"
            @toggle-submenu="item.onToggleSubmenu?.()" />
        </li>
      </ul>

      <!-- Phone bar (stacked): the settings entry belongs INSIDE the scroller —
           it is one more tab beside the sections, and the scroller then spans the
           whole bar instead of sharing it with a second flex column.
           A viewer has no settings tree at all: the tab goes with it (card
           t_803e86a4). -->
      <ul v-if="props.stacked && !isViewer" class="contents">
        <li class="min-w-16 flex-1">
          <NavButton
            variant="nav"
            :to="settingsItem.to"
            :icon="settingsItem.icon"
            :label="settingsItem.label"
            :show-label="showLabels"
            :stacked="props.stacked"
            :has-submenu="settingsItem.hasSubmenu"
            @toggle-submenu="openSettingsSidebar" />
        </li>
      </ul>
    </div>

    <!-- Sidebar: the settings entry stays pinned below the list, outside the
         scroller, so it cannot scroll away. Hidden entirely for a viewer (card
         t_803e86a4) — the whole tree it opens is admin territory. -->
    <ul
      v-if="!props.stacked && !isViewer"
      :class="
        props.horizontal
          ? 'ml-auto flex items-center gap-1 pl-2'
          : 'space-y-1 border-t border-gray-200 dark:border-gray-700'
      ">
      <li>
        <NavButton
          variant="nav"
          :to="settingsItem.to"
          :icon="settingsItem.icon"
          :label="settingsItem.label"
          :show-label="showLabels"
          :stacked="props.stacked"
          :has-submenu="settingsItem.hasSubmenu"
          @toggle-submenu="openSettingsSidebar" />
      </li>
    </ul>

    <!-- Create dashboard modal -->
    <CreateDashboardModal
      v-model="showCreateModal"
      :initial-layout-type="createLayoutType"
      @created="handleDashboardCreated" />

    <!-- Edit dashboard modal -->
    <EditDashboardModal
      v-if="editingDashboard"
      v-model="showEditModal"
      :dashboard="editingDashboard"
      @updated="handleDashboardUpdated"
      @deleted="handleDashboardDeletedFromModal" />

    <!-- Context menu -->
    <Teleport to="body">
      <template v-if="contextMenu.visible">
        <div class="fixed inset-0 z-40" @click="contextMenu.visible = false" />
        <div
          ref="contextMenuEl"
          :style="{ position: 'fixed', top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
          class="glass-surface z-50 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700"
          @mouseleave="contextMenu.visible = false">
          <button
            type="button"
            class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-900 transition-colors hover:bg-black/5 dark:text-white dark:hover:bg-white/10"
            @click="contextToggleVisibility">
            <span class="material-symbols-outlined text-base">{{
              contextMenu.row?.hidden ? 'visibility' : 'visibility_off'
            }}</span>
            {{ contextMenu.row?.hidden ? tt('Show', 'Pokaži') : tt('Hide', 'Skrij') }}
          </button>
          <button
            v-if="contextMenu.row?.kind === 'dashboard' && contextMayManagePage"
            type="button"
            class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-gray-900 transition-colors hover:bg-black/5 dark:text-white dark:hover:bg-white/10"
            @click="contextEditDashboard">
            <span class="material-symbols-outlined text-base">edit</span>
            {{ tt('Edit', 'Uredi') }}
          </button>
          <button
            v-if="contextMenu.row?.kind === 'group' || contextMayManagePage"
            type="button"
            class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
            @click="contextDelete">
            <span class="material-symbols-outlined text-base">delete</span>
            {{ tt('Delete', 'Izbriši') }}
          </button>
        </div>
      </template>
    </Teleport>
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import draggable from 'vuedraggable';
import NavButton from './NavButton.vue';
import CreateDashboardModal from './dashboard/CreateDashboardModal.vue';
import EditDashboardModal from './dashboard/EditDashboardModal.vue';
import IconPicker from './inputs/IconPicker.vue';
import { FIXED_NAV_DEFINITIONS, localizeNavigationDefinitions, prefixNavigationPath } from '../constants/navigation';
import { useNavigationTargets } from '../composables/useNavigationTargets';
import { useUIStore } from '../stores/ui';
import { useAuthStore } from '../stores/auth';
import { useSecurityStore } from '../stores/security';
import { useRoomsNav } from '../composables/useRoomsNav';
import { useSettingsNav } from '../composables/useSettingsNav';
import { useSidebarConfigStore } from '../stores/sidebarConfig';
import { useDashboardStore } from '../stores/dashboard';
import { useSocketStore } from '../stores/socket';
import type { Dashboard } from '@/types';
import { useMessageBox } from '../composables/useMessageBox';
import { isViewerRole, isAdminRole } from '../utils/roles';
import { tt } from '../utils/consoleText';

type NavRowKind = 'primary' | 'group' | 'dashboard' | 'page';

interface NavItemLike {
  id: string;
  icon: string;
  label: string;
  to?: string;
  hidden: boolean;
  badge?: number;
  badgeColor?: string;
  hasSubmenu?: boolean;
  onToggleSubmenu?: () => void;
  /** Security-state color (alarm=red, armed=green) like room navigation buttons. */
  state?: 'alarm' | 'armed' | 'pending' | 'supervise';
}

interface NavRow extends NavItemLike {
  key: string;
  kind: NavRowKind;
  order: number;
  editable: boolean;
}

interface NavContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  row: NavRow | null;
}

// Order fallbacks keep the list ordered when no sidebar config exists yet.
const MAIN_GROUP_ORDER_OFFSET = 100;
const MAIN_DASHBOARD_ORDER_OFFSET = 200;
const MAIN_PAGE_ORDER_OFFSET = 300;
const CONTEXT_MENU_MARGIN = 8;

const { t } = useI18n();
const { confirm: confirmBox } = useMessageBox();
const route = useRoute();
const router = useRouter();
const ui = useUIStore();
const authStore = useAuthStore();
const securityStore = useSecurityStore();
const sidebarConfig = useSidebarConfigStore();
const dashboardStore = useDashboardStore();
const socketStore = useSocketStore();
const { toggleSidebar: toggleRoomsSidebar } = useRoomsNav();
const { openSidebar: openSettingsSidebar } = useSettingsNav();
const { mainPageTargets, ungroupedDashboardTargets } = useNavigationTargets();

const navigationReady = ref(false);
const isEditMode = computed(() => ui.sidebarEditMode && authStore.isAdmin);

// ── The guest (viewer) experience (card t_803e86a4) ────────────────────────
// A viewer controls the house and never configures it: the settings tree and
// the maintenance surface are the app's two admin doors in this menu, and both
// are hidden here. Everything else a resident uses (dashboards, rooms, devices,
// cameras, map, media) stays exactly as it is — this is a hiding rule, not a
// permission model; the hub's own guard is the security layer (`utils/roles.ts`
// → `isViewerRole`, the hub's guest scope guard).
const VIEWER_HIDDEN_NAV_IDS = new Set<string>(['maintenance']);
const isViewer = computed(() => isViewerRole(authStore.user?.role));
/** May this identity see a main-list row? */
function viewerMaySeeRow(row: NavRow): boolean {
  return !isViewer.value || !VIEWER_HIDDEN_NAV_IDS.has(row.id);
}

/** Header layout (tablet portrait): items are laid out in a row, labels always on. */
const props = withDefaults(defineProps<{ horizontal?: boolean; stacked?: boolean }>(), {
  horizontal: false,
  stacked: false,
});
const showLabels = computed(() => props.horizontal || ui.sidebarExpanded || ui.mobileSidebarOpen);
const hubId = computed(() => route.params.hubId as string | undefined);

// ── Add dashboard / group ──────────────────────────────────────────────────
const addMenuOpen = ref(false);
const showCreateModal = ref(false);
const createLayoutType = ref<'column' | 'grid' | 'canvas' | 'bim'>('column');

// ── Edit dashboard modal ───────────────────────────────────────────────────
const showEditModal = ref(false);
const editingDashboard = ref<Dashboard | null>(null);

// ── Context menu ───────────────────────────────────────────────────────────
const contextMenu = ref<NavContextMenuState>({ visible: false, x: 0, y: 0, row: null });
const contextMenuEl = ref<HTMLElement | null>(null);

const mainItems = computed<NavItemLike[]>(() =>
  mainPageTargets.value.map((item) => ({
    id: item.id,
    icon: item.icon,
    label: item.label,
    to: item.route,
    hidden: !item.pinned,
    hasSubmenu: item.id === 'rooms',
    onToggleSubmenu: item.id === 'rooms' ? toggleRoomsSidebar : undefined,
    state: securityNavStateFor(item.id),
    ...badgeFor(item.id),
  })),
);

/** Alarm badge on Security. */
function badgeFor(id: string): { badge?: number; badgeColor?: string } {
  if (id === 'security' && securityStore.alarmActive) {
    return { badge: 1, badgeColor: 'bg-red-500 animate-pulse' };
  }
  return {};
}

/** Security state color for a nav item (like room navigation buttons). */
function securityNavStateFor(id: string): 'alarm' | 'armed' | 'pending' | 'supervise' | undefined {
  if (id !== 'security') return undefined;
  if (securityStore.alarmActive) return 'alarm';
  if (securityStore.activeModeId) return 'armed';
  return undefined;
}

const ungroupedDashboardItems = computed<NavItemLike[]>(() =>
  ungroupedDashboardTargets.value.map((item) => ({
    id: item.id,
    icon: item.icon,
    label: item.label,
    to: item.route,
    hidden: !item.pinned,
  })),
);

const settingsItem = computed(() => {
  const item = localizeNavigationDefinitions(
    FIXED_NAV_DEFINITIONS.filter((definition) => definition.id === 'settings'),
    t,
  )[0];
  return {
    id: item.id,
    icon: item.icon,
    label: item.label,
    to: prefixNavigationPath(item.path, hubId.value),
    hasSubmenu: item.hasSubmenu,
  };
});

// ── Rows (view + edit) ─────────────────────────────────────────────────────
function configOrderFor(id: string, fallback: number): number {
  const found = sidebarConfig.items.find((entry) => entry.id === id);
  return found?.order ?? fallback;
}

function toRow(item: NavItemLike, kind: NavRowKind, order: number, editable: boolean): NavRow {
  return {
    key: `${kind}:${item.id}`,
    kind,
    id: item.id,
    icon: item.icon,
    label: item.label,
    to: item.to,
    hidden: item.hidden,
    order,
    badge: item.badge,
    badgeColor: item.badgeColor,
    hasSubmenu: item.hasSubmenu,
    onToggleSubmenu: item.onToggleSubmenu,
    state: item.state,
    editable,
  };
}

const allMenuRows = computed<NavRow[]>(() => {
  const rows = [
    ...ungroupedDashboardItems.value.map((item, index) =>
      toRow(item, 'dashboard', configOrderFor(item.id, MAIN_DASHBOARD_ORDER_OFFSET + index), true),
    ),
    ...mainItems.value.map((item, index) =>
      toRow(item, 'page', configOrderFor(item.id, MAIN_PAGE_ORDER_OFFSET + index), false),
    ),
  ];

  // The old menu persisted a separate 0..N order for its house and personal lists,
  // so stored orders only compare across the whole menu once one merged drag has
  // written an order for every row. Until then the composed order is the menu order.
  const hasUnifiedOrder = rows.every((row) => sidebarConfig.items.some((entry) => entry.id === row.id));

  return hasUnifiedOrder ? [...rows].sort((left, right) => left.order - right.order) : rows;
});

const visibleRows = computed(() => allMenuRows.value.filter((row) => !row.hidden && viewerMaySeeRow(row)));

const editableRows = ref<NavRow[]>([]);

watch(
  allMenuRows,
  (rows) => {
    editableRows.value = [...rows];
  },
  { immediate: true },
);

// ── Reorder / visibility persistence ───────────────────────────────────────
function persistRows() {
  void persistRowsOrder(editableRows.value);
}

async function persistRowsOrder(rows: NavRow[]) {
  const next = rows.map((row, index) => ({ id: row.id, hidden: row.hidden, order: index }));
  const seen = new Set(next.map((entry) => entry.id));
  for (const item of sidebarConfig.items) {
    if (!seen.has(item.id)) next.push({ ...item });
  }
  await sidebarConfig.save(next);
}

async function toggleRowVisible(row: NavRow) {
  row.hidden = !row.hidden;
  try {
    await sidebarConfig.setHidden(row.id, row.hidden);
  } catch (err) {
    row.hidden = !row.hidden;
    console.error('Failed to toggle menu item visibility', err);
  }
}

// ── Add menu ───────────────────────────────────────────────────────────────
function startAddDashboard() {
  addMenuOpen.value = false;
  showCreateModal.value = true;
}

// ── Dashboard modal handlers ───────────────────────────────────────────────
function handleDashboardCreated(dashboard?: Dashboard) {
  // New dashboards/groups show up reactively from the dashboard store. Give a new
  // dashboard its own order entry so the merged menu keeps one order scale.
  if (dashboard?.id) {
    void sidebarConfig.setHidden(dashboard.id, false);
  }
}

function handleDashboardUpdated() {
  showEditModal.value = false;
  editingDashboard.value = null;
}

function handleDashboardDeletedFromModal() {
  const deletedId = String(editingDashboard.value?.id ?? '');
  showEditModal.value = false;
  editingDashboard.value = null;
  if (deletedId && route.params.id === deletedId) {
    void router.push(route.params.hubId ? `/h/${route.params.hubId}/dashboard` : '/dashboard');
  }
}

// ── Context menu ───────────────────────────────────────────────────────────
/**
 * The hub's shared 3D page (`t_hub_bim_model`) has no owner: every account may
 * open it and only an admin may edit or delete it. Another account's dashboard
 * is not managed here either — the hub answers 403 — but the menu keeps offering
 * what it always did for a user's OWN rows.
 */
const contextRowDashboard = computed(() => {
  const row = contextMenu.value.row;
  if (!row || row.kind !== 'dashboard') return null;
  return dashboardStore.dashboards.find((entry) => entry.id === row.id) ?? null;
});
const contextMayManagePage = computed(() => {
  const dashboard = contextRowDashboard.value;
  if (!dashboard) return true;
  if (dashboard.hubPage === true || dashboard.userId == null) {
    return isAdminRole(authStore.user?.role);
  }
  return true;
});

function openContextMenu(row: NavRow, event: MouseEvent) {
  contextMenu.value = { visible: true, x: event.clientX, y: event.clientY, row };

  nextTick(() => {
    const el = contextMenuEl.value;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const maxX = Math.max(0, window.innerWidth - width - CONTEXT_MENU_MARGIN);
    const maxY = Math.max(0, window.innerHeight - height - CONTEXT_MENU_MARGIN);
    contextMenu.value.x = Math.min(contextMenu.value.x, maxX);
    contextMenu.value.y = Math.min(contextMenu.value.y, maxY);
  });
}

function contextToggleVisibility() {
  const row = contextMenu.value.row;
  if (!row) return;
  contextMenu.value.visible = false;
  void toggleRowVisible(row);
}

function contextEditDashboard() {
  const row = contextMenu.value.row;
  if (!row) return;
  contextMenu.value.visible = false;
  const dashboard = dashboardStore.dashboards.find((entry) => entry.id === row.id);
  if (!dashboard) return;
  editingDashboard.value = dashboard;
  showEditModal.value = true;
}

async function contextDelete() {
  const row = contextMenu.value.row;
  if (!row) return;
  contextMenu.value.visible = false;

  const message = t('dialogs.dashboardDeleteConfirm', { name: row.label });
  const ok = await confirmBox(message);
  if (!ok) return;

  try {
    await dashboardStore.deleteDashboard(row.id);
    if (route.params.id === row.id) {
      await router.push(route.params.hubId ? `/h/${route.params.hubId}/dashboard` : '/dashboard');
    }
  } catch (err) {
    console.error('Failed to delete menu item', err);
  }
}

/**
 * Pages whose chrome is not a hub's own: the menu loads nothing for them (it is
 * barely mounted on them at all — the check is the mount's own).
 */
function navigationAppliesToCurrentRoute(): boolean {
  return !(
    route.path === '/cloud' ||
    route.path === '/home' ||
    route.path === '/servers' ||
    (route.meta.cloudView && !route.params.hubId)
  );
}

let navigationLoad: Promise<void> | null = null;

/**
 * Load what the menu renders: pins/order (sidebar config), the dashboards
 * behind the primary entries and the dashboard groups. Deduplicated, so the
 * mount and the recovery watchers below can ask at once without double-fetching.
 */
function ensureNavigationData(): Promise<void> {
  if (!navigationLoad) {
    navigationLoad = (async () => {
      try {
        await Promise.all([
          sidebarConfig.loaded ? Promise.resolve() : sidebarConfig.load(),
          dashboardStore.dashboards.length > 0 ? Promise.resolve() : dashboardStore.loadDashboards(route.params.hubId),
        ]);
      } finally {
        navigationReady.value = true;
        navigationLoad = null;
      }
    })();
  }
  return navigationLoad;
}

onMounted(async () => {
  if (!navigationAppliesToCurrentRoute()) {
    navigationReady.value = true;
    return;
  }

  // Subscribe to security state so the Security menu item shows the alarm badge.
  securityStore.initSocketListeners();
  void securityStore.fetchConfig();

  await ensureNavigationData();
});

/**
 * The menu loads its data once per mount, but a hub (re)connect can clear it
 * under a mounted menu without navigating anywhere: `resetForHubSwitch` runs on
 * every silent (re)connect — the LAN route handing the session to the tunnel
 * (`services/hubLocalRoute.ts`), a "Reconnect" that resumes on the same route
 * (`stores/connectionLost.ts`) — and nothing reloads the dashboards afterwards,
 * leaving the menu short of its dashboard items until a full page reload. Heal
 * it here: reload when the hub scope changes, when the stores this menu renders
 * were emptied, or when the hub connection is (re)established with the data
 * still missing (a boot-time load that failed while the hub was unreachable).
 */
watch(hubId, () => {
  if (!navigationAppliesToCurrentRoute()) return;
  void ensureNavigationData();
});

watch(
  () => dashboardStore.dashboards.length,
  (next, prev) => {
    if (!navigationAppliesToCurrentRoute()) return;
    const hadItems = (prev ?? 0) > 0;
    const emptied = next === 0;
    if (hadItems && emptied) void ensureNavigationData();
  },
);

watch(
  () => socketStore.connected,
  (connected, wasConnected) => {
    if (!connected || wasConnected) return;
    if (!navigationAppliesToCurrentRoute()) return;
    void ensureNavigationData();
  },
);
</script>

<style scoped>
.hide-scrollbar {
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
